import React, { useMemo, useState, useEffect } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { SlidersHorizontal, ChevronDown } from "lucide-react";
import { categories, brands } from "../mock";
import { fetchProducts } from "../lib/api";
import ProductCard from "../components/ProductCard";

const sortOptions = [
  { value: "featured", label: "Em destaque" },
  { value: "price-asc", label: "Preço: baixo para alto" },
  { value: "price-desc", label: "Preço: alto para baixo" },
  { value: "rating", label: "Melhor avaliados" },
  { value: "discount", label: "Maior desconto" },
];

export default function Shop() {
  const { category } = useParams();
  const [searchParams] = useSearchParams();
  const query = searchParams.get("q")?.toLowerCase() || "";

  const [sort, setSort] = useState("featured");
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [maxPrice, setMaxPrice] = useState(150);
  const [showFilters, setShowFilters] = useState(false);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    fetchProducts().then(setProducts).catch(() => setProducts([]));
  }, []);

  const activeCategory = categories.find((c) => c.slug === category);

  const filtered = useMemo(() => {
    let list = [...products];
    if (category) list = list.filter((p) => p.category === category);
    if (query)
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(query) ||
          p.brand.toLowerCase().includes(query) ||
          p.subtitle.toLowerCase().includes(query)
      );
    if (selectedBrands.length)
      list = list.filter((p) => selectedBrands.includes(p.brand));
    list = list.filter((p) => p.price <= maxPrice);

    switch (sort) {
      case "price-asc":
        list.sort((a, b) => a.price - b.price);
        break;
      case "price-desc":
        list.sort((a, b) => b.price - a.price);
        break;
      case "rating":
        list.sort((a, b) => b.rating - a.rating);
        break;
      case "discount":
        list.sort(
          (a, b) =>
            (b.oldPrice ? 1 - b.price / b.oldPrice : 0) -
            (a.oldPrice ? 1 - a.price / a.oldPrice : 0)
        );
        break;
      default:
        break;
    }
    return list;
  }, [category, query, selectedBrands, maxPrice, sort, products]);

  const toggleBrand = (b) =>
    setSelectedBrands((prev) =>
      prev.includes(b) ? prev.filter((x) => x !== b) : [...prev, b]
    );

  const Filters = () => (
    <div className="space-y-8">
      <div>
        <h4 className="font-medium mb-3 text-sm">Categorias</h4>
        <ul className="space-y-2 text-sm">
          <li>
            <Link
              to="/loja"
              className={`hover:text-primary ${!category ? "text-primary font-medium" : "text-muted-foreground"}`}
            >
              Todos os produtos
            </Link>
          </li>
          {categories.map((c) => (
            <li key={c.id}>
              <Link
                to={`/loja/${c.slug}`}
                className={`hover:text-primary ${category === c.slug ? "text-primary font-medium" : "text-muted-foreground"}`}
              >
                {c.name}
              </Link>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <h4 className="font-medium mb-3 text-sm">Marca</h4>
        <ul className="space-y-2.5">
          {brands.map((b) => (
            <li key={b} className="flex items-center gap-2">
              <input
                id={`brand-${b}`}
                type="checkbox"
                checked={selectedBrands.includes(b)}
                onChange={() => toggleBrand(b)}
                className="h-4 w-4 rounded border-border accent-[hsl(12,62%,58%)]"
              />
              <label htmlFor={`brand-${b}`} className="text-sm text-muted-foreground cursor-pointer">
                {b}
              </label>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <h4 className="font-medium mb-3 text-sm">
          Preço máximo: <span className="text-primary">€ {maxPrice}</span>
        </h4>
        <input
          type="range"
          min="10"
          max="150"
          value={maxPrice}
          onChange={(e) => setMaxPrice(Number(e.target.value))}
          className="w-full accent-[hsl(12,62%,58%)]"
        />
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      {/* Breadcrumb + title */}
      <nav className="text-sm text-muted-foreground mb-4">
        <Link to="/" className="hover:text-primary">Início</Link>
        <span className="mx-2">/</span>
        <Link to="/loja" className="hover:text-primary">Loja</Link>
        {activeCategory && (
          <>
            <span className="mx-2">/</span>
            <span className="text-foreground">{activeCategory.name}</span>
          </>
        )}
      </nav>

      <div className="mb-8">
        <h1 className="font-serif text-3xl md:text-4xl">
          {query ? `Resultados para "${query}"` : activeCategory ? activeCategory.name : "Todos os produtos"}
        </h1>
        <p className="text-muted-foreground mt-1">{filtered.length} produtos</p>
      </div>

      <div className="flex gap-8">
        {/* Sidebar */}
        <aside className="hidden md:block w-56 shrink-0">
          <Filters />
        </aside>

        {/* Main */}
        <div className="flex-1">
          <div className="flex items-center justify-between mb-6 gap-3">
            <button
              onClick={() => setShowFilters((s) => !s)}
              className="md:hidden flex items-center gap-2 text-sm border border-border rounded-full px-4 h-10"
            >
              <SlidersHorizontal className="h-4 w-4" /> Filtros
            </button>
            <div className="ml-auto relative">
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="appearance-none bg-secondary rounded-full pl-4 pr-10 h-10 text-sm outline-none cursor-pointer"
              >
                {sortOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <ChevronDown className="h-4 w-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
          </div>

          {showFilters && (
            <div className="md:hidden mb-6 p-5 border border-border rounded-lg">
              <Filters />
            </div>
          )}

          {filtered.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-lg font-medium">Nenhum produto encontrado</p>
              <p className="text-muted-foreground mt-1">Tenta ajustar os filtros.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {filtered.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
